<template>
  <form class="payment__card">
    <AmCreditCard
      ref="creditCard"
      :label="$t('steps.payment.card.cardNumber')"
      :selectedCardType="selectedCardType"
      @updateCardType="setSelectedCardType"
      @updateCard="updateCard"
    />
    <AmCardList
      class="input--custom"
      :mapper-country-to-cards="mapperCountryToCards"
      :currency-code="currencyCode"
    />
    <AmInput
      v-model="payment.name"
      :test-name="'nome'"
      class="input--custom"
      :label="$t('steps.payment.card.name.label')"
      :placeholder="$t('steps.payment.card.name.placeholder')"
      :validity-state="statusNameValidate"
      :secondary-label="textNameValidate"
      @input="validateName"
    />
    <DocumentInput
      v-model="payment.document"
      isPaymentCardStep
      @validate="handleDocumentInput"
    />
    <div
      v-if="isCreditCard"
      class="position-relative input-container input--custom"
    >
      <label class="am-input-label mt-3">
        {{ $t('steps.payment.card.installment.label') }}
      </label>
      <select
        v-model="payment.installments"
        class="select-installments"
        :required="true"
        @change="setInstallmentChangeToStore"
      >
        <option
          v-for="installment in installments"
          :key="installment.id"
          :value="installment"
        >
          {{ installment.label }}
        </option>
      </select>
      <AmIconMaterial
        type="round"
        name="expand_more"
        size="12"
      />
    </div>
    <div
      v-if="showRecaptcha()"
      class="recaptcha"
    >
      <div
        v-if="recaptchaErrorShow"
        class="recaptcha-show recaptcha-error"
      >
        <span>Assinale não sou um robô</span>
      </div>
      <div class="recaptcha-show">
        <vue-recaptcha
          ref="recaptcha"
          :sitekey="recaptchaKey"
          @verify="verifyRecaptcha"
          @expired="expiredRecaptcha"
        />
      </div>
    </div>
  </form>
</template>

<script>
import AmCreditCard from '@/components/molecules/AmCreditCard.vue'
import AmCardList from '@/components/molecules/AmCardList.vue'

import currencyFilterMixin from '@/components/mixins/currencyFilterMixin'
import { mapActions, mapMutations, mapGetters, mapState } from 'vuex'
import { VueRecaptcha } from 'vue-recaptcha'
import replaceSpecialCharactersMixin from '@/components/mixins/replaceSpecialCharactersMixin'
import queryPersistance from '@/components/mixins/queryPersistance'
import sendToFlashlight from '@/utils/flashlightService'
import DocumentInput from '../common/DocumentInput.vue'

import mountInstallment from '../utils/mountInstallment'

export default {
  name: 'PaymentCard',
  components: {
    AmCreditCard,
    VueRecaptcha,
    AmCardList,
    DocumentInput,
  },
  mixins: [currencyFilterMixin, replaceSpecialCharactersMixin, queryPersistance],
  props: {
    value: {
      type: Object,
      default: () => {
        return {
          name: null,
          credit_card_number: null,
          document: null,
          credit_card_year: null,
          credit_card_month: null,
          credit_card_cvv: null,
          installments: 1,
          recaptcha_response: null,
          recaptcha_attempts: 0,
        }
      },
    },
  },
  data() {
    return {
      selectedCardType: 'credito',
      creditCard: {
        number: null,
        shelfLife: null,
        cpf: null,
        cvv: null,
      },
      statusNameValidate: null,
      textNameValidate: '',
      recaptchaKey: process.env.VUE_APP_RECAPTCHA_KEY.replace(/"/g, ''),
      recaptchaEnabled: process.env.VUE_APP_RECAPTCHA_ENABLED.replace(/"/g, ''),
      recaptchaMarked: false,
      recaptchaMaxAttempts: process.env.VUE_APP_RECAPTCHA_MAX_ATTEMPTS.replace(/"/g, ''),
      recaptchaErrorShow: false,
      payment: {
        credit_card_number: null,
        name: null,
        document: null,
        credit_card_year: null,
        credit_card_month: null,
        credit_card_cvv: null,
        installments: null,
        recaptcha_response: null,
        recaptcha_attempts: 0,
      },
      creditCardFlag: '',
      creditCardLastDigits: '',
      errors: {
        creditCard: true,
        name: true,
        recaptcha: true,
        document: null,
      },
      mapperCountryToCards: {
        others: ['visa', 'master'],
        BRL: [],
        CLP: ['master', 'visa', 'amex', 'diners', 'magna', 'crm', 'presto'],
        COP: ['master', 'visa', 'amex', 'diners'],
        MXN: ['master', 'visa', 'amex', 'carnet'],
      },
    }
  },
  computed: {
    ...mapGetters('Card', ['isMultiplePayments', 'cardType']),
    ...mapState('Checkout', {
      cart: (state) => state.cart,
    }),
    ...mapGetters('Checkout', ['getPaymentCard']),
    ...mapGetters('Cart', [
      'getCurrencyCode',
      'getInstallmentDefault',
      'getInstallments',
      'getSelectedInstallment',
      'isInternational',
      'getTotal',
      'isInfoProduto',
      'getIsInternational',
      'getRecaptchaAttempts',
    ]),
    ...mapGetters('Customer', ['getDocumentNumber']),
    installments() {
      return this.getInstallments.map((installments) =>
        mountInstallment({
          properties: installments,
          isInternational: this.getIsInternational,
          currencyCode: this.getCurrencyCode,
          total: this.getTotal,
          currencyFilter: this.$options.filters.currency,
        })
      )
    },
    currencyCode() {
      return this.getCurrencyCode !== 'CLP' &&
        this.getCurrencyCode !== 'COP' &&
        this.getCurrencyCode !== 'MXN' &&
        this.getCurrencyCode !== 'BRL'
        ? 'others'
        : this.getCurrencyCode
    },
    showDocument() {
      return !this.getIsInternational
    },
    isCreditCard() {
      return (
        !this.getIsInternational ||
        this.cardType === 'credito' ||
        (this.cardType === 'multiplo' && this.selectedCardType === 'credito')
      )
    },
  },
  watch: {
    errors: {
      deep: true,
      handler() {
        this.validateForm()
      },
    },
    getInstallments: {
      immediate: true,
      handler() {
        this.setInstallment()
      },
    },
    getCurrencyCode(value) {
      if (value !== 'BRL' && !this.getDocumentNumber) {
        this.$router.push({ name: 'new.dataStep', query: { pl: this.getPl?.pl } })
      }
    },
  },
  mounted() {
    this.SET_CURRENT_STEP(4)
    this.errors.recaptcha = this.getRecaptchaAttempts >= 3
  },
  destroyed() {
    this.setNullOnSelectedInstallmentWhenBack()
  },
  methods: {
    ...mapActions('Card', ['resetCard']),
    ...mapMutations('Checkout', [
      'SET_CURRENT_STEP',
      'SET_PAYMENT_CARD',
      'SET_DISABLED_STATE_PAYMENT',
    ]),
    ...mapMutations('Cart', {
      setSelectedInstallment: 'SET_SELECTED_INSTALLMENT',
    }),
    ...mapActions('Cart', ['paymentCreditCard']),
    setSelectedCardType(value) {
      this.selectedCardType = value
    },
    setNullOnSelectedInstallmentWhenBack() {
      if (
        (this.getIsInternational && this.$route.name === 'new.dataStep') ||
        this.$route.name === 'new.stepPayment'
      ) {
        this.setSelectedInstallment(null)
      }
    },
    setInstallment() {
      this.payment.installments = mountInstallment({
        properties: this.searchPaymentByInstallment(this.getInstallmentDefault),
        isInternational: this.getIsInternational,
        currencyCode: this.getCurrencyCode,
        total: this.getTotal,
        currencyFilter: this.$options.filters.currency,
      })

      this.setInstallmentChangeToStore()
    },
    searchPaymentByInstallment(idPayment) {
      return this.getInstallments.find(
        (installmentProperties) => installmentProperties.installment === idPayment
      )
    },
    setInstallmentChangeToStore() {
      this.setSelectedInstallment(this.payment.installments)
    },
    checkCreditCardData(creditCardData) {
      this.creditCardFlag = creditCardData.creditCardBrand
      this.creditCardLastDigits = creditCardData.creditCardLastDigits.slice(-4)
    },
    handleDocumentInput(validation) {
      this.errors.document = validation ? 'success' : 'error'
    },
    validateForm() {
      if (this.getIsInternational) this.errors.document = 'success'
      const hasAnyError = !!Object.entries(this.errors).find(
        (e) => e[1] === true || e[1] === 'error' || e[1] === null
      )
      if (hasAnyError) {
        this.SET_DISABLED_STATE_PAYMENT(true)
        return
      }

      this.SET_DISABLED_STATE_PAYMENT(false)
    },
    getCreditCardDetails({ creditCardDetails: { creditCardBrand, creditCardLastDigits }, type }) {
      return {
        creditCardFlag: creditCardBrand,
        creditCardLastDigits,
        paymentType: type,
        installmentSelected: this.payment.installments,
      }
    },
    updateCard(creditCard) {
      if (creditCard.hasCVVError || creditCard.hasNumberError || creditCard.hasShelfLifeError) {
        this.errors.creditCard = true
        return
      }

      const [month = null, year = null] = creditCard.shelfLife.split('/')

      const paymentCreditCardValue = {
        ...this.payment,
        credit_card_number: creditCard.number,
        credit_card_year: year,
        credit_card_month: month,
        credit_card_cvv: creditCard.cvv,
      }
      this.errors.creditCard = false
      this.payment = paymentCreditCardValue
      this.SET_PAYMENT_CARD(paymentCreditCardValue)
    },
    getPayloadInternationalCustomer(payment) {
      const ONE_PAYMENT = 1
      const document = this.removeSpecialCharaters(this.getDocumentNumber)
      const installments = this.isCreditCard ? this.payment.installments.id : ONE_PAYMENT
      return {
        ...payment,
        installments,
        credit_card_number: payment.credit_card_number,
        document,
      }
    },
    getPayloadNationalCustomer(payment) {
      const documentWithoutMask = this.removeSpecialCharaters(payment.document)
      return {
        ...payment,
        installments: this.payment.installments.id,
        credit_card_number: payment.credit_card_number,
        document: documentWithoutMask,
      }
    },
    async paymentProcess() {
      try {
        const payloadCustomer = this.showDocument
          ? this.getPayloadNationalCustomer(this.payment)
          : this.getPayloadInternationalCustomer(this.payment)
        const {
          data: { data },
        } = await this.paymentCreditCard(payloadCustomer)

        this.checkCreditCardData(data.creditCardDetails)
        const creditCardDetails = this.getCreditCardDetails(data)

        const payload = {
          cart: this.cart.hash,
          payment_type: 'credit_card',
        }

        sendToFlashlight(payload)

        this.$router.push({
          name: 'new.stepPaymentSuccess',
          params: { history: true, creditCardDetails },
          query: { pl: this.getPl?.pl },
        })
      } catch (error) {
        this.$router.push({
          name: 'new.stepPaymentError',
          params: { history: true },
          query: { pl: this.getPl?.pl },
        })
        this.hideButtonActionPayment = false
        this.hideReturnStepPayment = true
      }
    },
    previousStep() {
      this.setNullOnSelectedInstallmentWhenBack()
      this.SET_DISABLED_STATE_PAYMENT(false)
      if (this.getIsInternational && this.isInfoProduto) {
        this.$router.push({ name: 'new.dataStep', query: { pl: this.getPl?.pl } })
        return
      }
      this.$router.push({ name: 'new.stepPayment', query: { pl: this.getPl?.pl } })
    },
    nextStep() {
      return new Promise((resolve) => {
        this.$router.push({
          name: 'new.stepPaymentCardLoading',
          query: { pl: this.getPl?.pl },
        })
        try {
          this.paymentProcess()
          resolve()
        } catch (e) {
          throw new Error(e)
        }
      })
    },
    validateName() {
      const isValidName = this.checkIfNameIsValid(this.payment.name)

      if (!isValidName) {
        this.statusNameValidate = 'error'
        this.textNameValidate = this.$t('steps.validators.required')
        this.errors.name = true
        return false
      }

      this.statusNameValidate = 'success'
      this.textNameValidate = ''
      this.errors.name = false
      return true
    },
    checkIfNameIsValid(name) {
      const names = name.split(' ')
      const hasTwoNamesValid = names.length > 1 && names[0].length >= 2 && names[1].length > 0
      if (!hasTwoNamesValid) return false

      const nameWithoutAccents = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      const regex = /^(?:[a-z][.']?\s?)+$/gi
      return regex.test(nameWithoutAccents)
    },
    verifyRecaptcha(recaptchaToken) {
      this.recaptchaMarked = true
      this.errors.recaptcha = false
      this.payment.recaptcha_response = recaptchaToken
      this.$emit('recaptchaToken', recaptchaToken)
    },
    expiredRecaptcha() {
      this.recaptchaMarked = false
      this.$refs.recaptcha.reset()
    },
    validateRecaptcha() {
      if (!this.showRecaptcha()) {
        this.recaptchaErrorShow = false
        return true
      }

      if (!this.recaptchaMarked) {
        this.recaptchaErrorShow = true
        this.$el.querySelector('.recaptcha-show div').classList.add('recaptcha-iframe-error')
        return false
      }

      this.recaptchaErrorShow = false
      return true
    },
    showRecaptcha() {
      if (!this.recaptchaEnabled) {
        return false
      }
      return (
        this.recaptchaMaxAttempts === 0 || this.getRecaptchaAttempts >= this.recaptchaMaxAttempts
      )
    },
  },
}
</script>

<style lang="scss" scoped>
.payment__card {
  width: 100%;
}

.input-container {
  .select-installments {
    padding-left: 8px;
    padding-right: 28px;
    height: 42px;
    color: #333333;
    border: 1px solid #d2d6de;
    width: 100%;
    font-size: 14px;
    line-height: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    border-radius: 5px;
    grid-template-areas: 'select';
    align-items: center;
    display: grid;
    position: relative;
    -moz-appearance: none;
    -webkit-appearance: none;
    appearance: none;
    background-color: #fff;
  }

  span {
    position: absolute;
    bottom: 8px;
    right: 5px;
    pointer-events: none;
  }
}

.recaptcha {
  justify-content: center;
  margin-top: 25px;
}

.recaptcha-error {
  color: #dd4b39 !important;
  margin-left: 29%;
  font-size: 12px;
}

.recaptcha-show {
  justify-content: center;
  display: flex;
}

.recaptcha-iframe-error {
  border: 1px solid #dd4b3b;
  border-radius: 2px;
}
</style>
